// ---------------------------------------------------------------------------
// Container Renderer (PB-E)
// ---------------------------------------------------------------------------
// Zone-bearing primitive — wraps a vertical stack of child blocks in a
// constrained, optionally-padded section. Children are passed through
// `props.children` as a Puck component array and rendered recursively via
// `ctx.renderBlock`.

import { registerRenderer } from "../registry.js";
import type { PuckComponent, RenderContext } from "../types.js";
import { attr } from "../escape.js";

type ContainerAlign = "left" | "center" | "right";
type ContainerPadding = "none" | "sm" | "md" | "lg" | "xl";

interface ContainerProps {
  children?: unknown;
  maxWidth?: string;
  padding?: ContainerPadding | string;
  align?: ContainerAlign | string;
  background?: string;
}

const VALID_ALIGN: ReadonlySet<ContainerAlign> = new Set([
  "left",
  "center",
  "right",
]);

const VALID_PADDING: ReadonlySet<ContainerPadding> = new Set([
  "none",
  "sm",
  "md",
  "lg",
  "xl",
]);

const SAFE_MAX_WIDTH = /^(?:\d{1,5}(?:px|rem|em|%|vw)|auto|none)$/i;
const SAFE_CSS_COLOR =
  /^(?:#[0-9a-f]{3,8}|rgb[a]?\([^)]{1,80}\)|hsl[a]?\([^)]{1,80}\)|[a-z]{3,20})$/i;

function resolveAlign(value: unknown): ContainerAlign {
  return typeof value === "string" && VALID_ALIGN.has(value as ContainerAlign)
    ? (value as ContainerAlign)
    : "left";
}

function resolvePadding(value: unknown): ContainerPadding {
  return typeof value === "string"
    && VALID_PADDING.has(value as ContainerPadding)
    ? (value as ContainerPadding)
    : "md";
}

function resolveMaxWidth(value: unknown): string {
  if (typeof value !== "string") return "1200px";
  const trimmed = value.trim();
  return SAFE_MAX_WIDTH.test(trimmed) ? trimmed : "1200px";
}

function resolveBackground(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return SAFE_CSS_COLOR.test(trimmed) ? trimmed : null;
}

function coerceChildren(value: unknown): PuckComponent[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is PuckComponent =>
      typeof entry === "object"
      && entry !== null
      && typeof (entry as PuckComponent).type === "string",
  );
}

function renderChildren(
  children: PuckComponent[],
  ctx: RenderContext,
): string {
  if (!ctx.renderBlock) return "";
  return children.map((child) => ctx.renderBlock!(child)).join("\n");
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  const p = props as ContainerProps;
  const children = coerceChildren(p.children);
  const align = resolveAlign(p.align);
  const padding = resolvePadding(p.padding);
  const maxWidth = resolveMaxWidth(p.maxWidth);
  const background = resolveBackground(p.background);

  const inner = renderChildren(children, ctx);
  const styleParts = [`max-width: ${maxWidth}`, `text-align: ${align}`];
  if (background) styleParts.push(`background: ${background}`);
  const style = styleParts.join("; ");

  return `<section class="ease-section ease-container ease-container--pad-${attr(padding)}" style="${attr(style)}">
  <div class="ease-container__inner">
${inner || "    <!-- container empty -->"}
  </div>
</section>`;
}

registerRenderer("container", { render });
